/**
 * Timeline Page Component
 * =======================
 *
 * Full-screen horizontal timeline where each event fills the viewport.
 * Navigate between events with arrow keys or explicit next/previous controls.
 * Event media can be swiped independently inside each slide.
 *
 * Consolidated component - EventSlide logic is inline.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'react-feather';
import { getImageUrl } from '../ui/OptimizedImage';
import { debugLog } from '../../utils/debug';
import MetaTags, { getAboutMetaData } from '../ui/MetaTags';
import Loading from '../ui/Loading';
import './TimelinePage.scss';

// Utility function for formatting dates
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

// Utility function to create URL-friendly slug from title
const slugify = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces, underscores, hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

// Event Slide Component (inline) - memoized for performance
const EventSlide = React.memo(({ event, isActive, pressItems = [], isMobile = false, events = [], navigateToIndex, getFirstEventImage, slugify, formatDate, prevEvent, nextEvent, currentIndex, eventsLength, showInlineNavigation = true }) => {
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const [mediaItems, setMediaItems] = useState([]);
  const [pressImageUrls, setPressImageUrls] = useState({});
  const [hoveredPress, setHoveredPress] = useState(null);
  const thumbStripRef = useRef(null);
  const mediaGestureRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    direction: null
  });

  const isVideoMedia = (item) => {
    if (!item) return false;
    if (item.type === 'video') return true;
    const path = item.path || '';
    return /\.(mp4|mov|m4v|webm|ogg)$/i.test(path);
  };

  const normalizeMediaPath = (path = '') => path.replace(/^\/+/, '');

  const filterPosterDuplicates = (mediaList = []) => {
    if (!Array.isArray(mediaList) || mediaList.length === 0) {
      return [];
    }

    const videoPosters = new Set(
      mediaList
        .filter((item) => isVideoMedia(item) && item.poster)
        .map((item) => normalizeMediaPath(item.poster))
    );

    return mediaList.filter((item) => {
      if (!item) return false;

      const isImage =
        item.type === 'image' ||
        (!item.type && /\.(jpg|jpeg|png|gif|webp|avif)$/i.test((item.jpeg || item.path || '')));

      if (!isImage) {
        return true;
      }

      const imagePath = normalizeMediaPath(item.jpeg || item.path || '');
      if (!imagePath) {
        return true;
      }

      return !videoPosters.has(imagePath);
    });
  };

  // Load event media (images + videos)
  useEffect(() => {
    let isCancelled = false;

    const loadMedia = async () => {
      const rawMedia = (event.preview_media && event.preview_media.length > 0)
        ? event.preview_media
        : event.preview_images || [];

      // console.log('[TimelinePage] Event:', event.title, 'rawMedia:', rawMedia, 'preview_media:', event.preview_media, 'preview_images:', event.preview_images);

      const filteredMedia = filterPosterDuplicates(rawMedia);

      // console.log('[TimelinePage] Filtered media:', filteredMedia);

      if (!filteredMedia || filteredMedia.length === 0) {
        if (!isCancelled) {
          setMediaItems([]);
          setActiveMediaIndex(0);
        }
        return;
      }

      const resolved = await Promise.all(
        filteredMedia.map(async (item) => {
          // Explicitly check for video type first
          if (item.type === 'video' || isVideoMedia(item)) {
            const path = item.path || '';
            const videoUrl = path.startsWith('http')
              ? path
              : `/assets/videos/${path.replace(/^\/?assets\/videos\//, '')}`;
            let posterUrl = null;
            if (item.poster) {
              posterUrl = await getImageUrl(item.poster);
            }
            const resolvedItem = {
              ...item,
              type: 'video', // Explicitly set type
              url: videoUrl,
              posterUrl,
              thumbnailUrl: posterUrl || null,
              format: item.format || 'video/mp4'
            };
            // console.log('[TimelinePage] Resolved video:', resolvedItem);
            return resolvedItem;
          }

          // Handle images
          const imagePath = item.jpeg || item.path;
          const imageUrl = await getImageUrl(imagePath);
          const resolvedItem = {
            ...item,
            type: 'image', // Explicitly set type
            url: imageUrl,
            thumbnailUrl: imageUrl
          };
          // console.log('[TimelinePage] Resolved image:', resolvedItem);
          return resolvedItem;
        })
      );

      if (!isCancelled) {
        setMediaItems(resolved);
        setActiveMediaIndex(0);
      }
    };

    loadMedia();

    return () => {
      isCancelled = true;
    };
  }, [event.preview_media, event.preview_images, event.id]);

  // Find related press items
  const relatedPress = event.press_refs
    ? pressItems.filter(press => event.press_refs.includes(press.id))
    : [];

  // Load press preview images
  useEffect(() => {
    const loadPressImages = async () => {
      const urls = {};
      for (const press of relatedPress) {
        if (press.preview_image) {
          const imagePath = press.preview_image.jpeg || press.preview_image.path;
          if (imagePath) {
            const url = await getImageUrl(imagePath);
            urls[press.id] = url;
          }
        }
      }
      setPressImageUrls(urls);
    };
    if (relatedPress.length > 0) {
      loadPressImages();
    }
  }, [relatedPress.length]); // Only depend on length to prevent infinite loops

  // Thumbnail strip scrolling
  const scrollThumbs = (direction) => {
    if (thumbStripRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      thumbStripRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Extract collaborators from event data (enriched with details from collaborators.json)
  const getCollaborators = () => {
    if (!event.collaborators || !Array.isArray(event.collaborators)) {
      return [];
    }

    return event.collaborators.map(collab => {
      // Use role if available, otherwise fallback to profession
      const role = collab.role || (collab.profession && collab.profession.length > 0 ? collab.profession[0] : 'Collaborator');
      return {
        collaborator_id: collab.collaborator_id,
        name: collab.name || 'Unknown',
        role: role,
        link: collab.link || null,
        bio: collab.bio || null
      };
    });
  };

  // Group collaborators by role
  const groupCollaboratorsByRole = (collaborators) => {
    const grouped = {};
    collaborators.forEach(collab => {
      const role = collab.role || 'Collaborator';
      if (!grouped[role]) {
        grouped[role] = [];
      }
      grouped[role].push(collab);
    });
    return grouped;
  };

  const collaborators = getCollaborators();
  const groupedCollaborators = groupCollaboratorsByRole(collaborators);

  // Clean description text (remove credit lines)
  const cleanDescription = (text) => {
    if (!text) return '';
    // Remove credit lines from description
    const lines = text.split('\n');
    const descriptionLines = lines.filter(line => {
      const lowerLine = line.toLowerCase();
      return !(lowerLine.includes('concept') && lowerLine.includes(':')) &&
             !(lowerLine.includes('credits') && lowerLine.includes(':')) &&
             !lowerLine.includes('photography:') &&
             !lowerLine.includes('movement:') &&
             !lowerLine.includes('sound:');
    });

    return descriptionLines.join('\n').trim();
  };


  const currentMedia = mediaItems[activeMediaIndex];
  const canNavigateMediaBack = activeMediaIndex > 0;
  const canNavigateMediaForward = activeMediaIndex < mediaItems.length - 1;

  const navigateMedia = useCallback((direction) => {
    setActiveMediaIndex((prev) => {
      if (direction < 0) {
        return Math.max(0, prev - 1);
      }
      if (direction > 0) {
        return Math.min(mediaItems.length - 1, prev + 1);
      }
      return prev;
    });
  }, [mediaItems.length]);

  const resetMediaGesture = useCallback((target) => {
    const activePointerId = mediaGestureRef.current.pointerId;
    if (target?.releasePointerCapture && activePointerId != null) {
      try {
        target.releasePointerCapture(activePointerId);
      } catch (error) {
        // Ignore capture release failures from stale pointers.
      }
    }
    mediaGestureRef.current = {
      active: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      direction: null
    };
  }, []);

  const handleMediaPointerDown = useCallback((e) => {
    if (mediaItems.length <= 1) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target instanceof Element && e.target.closest('.image-nav')) return;

    mediaGestureRef.current = {
      active: true,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      direction: null
    };

    if (e.currentTarget.setPointerCapture) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (error) {
        // Ignore pointer capture errors on unsupported environments.
      }
    }
  }, [mediaItems.length]);

  const handleMediaPointerMove = useCallback((e) => {
    const gesture = mediaGestureRef.current;
    if (!gesture.active || gesture.pointerId !== e.pointerId) return;

    const deltaX = e.clientX - gesture.startX;
    const deltaY = e.clientY - gesture.startY;

    if (!gesture.direction) {
      if (Math.abs(deltaX) < 12 && Math.abs(deltaY) < 12) {
        return;
      }

      gesture.direction = Math.abs(deltaX) > Math.abs(deltaY) * 1.2 ? 'horizontal' : 'vertical';
    }

    if (gesture.direction === 'horizontal' && e.cancelable) {
      e.preventDefault();
    }
  }, []);

  const handleMediaPointerUp = useCallback((e) => {
    const gesture = mediaGestureRef.current;
    if (!gesture.active || gesture.pointerId !== e.pointerId) return;

    const deltaX = e.clientX - gesture.startX;
    const deltaY = e.clientY - gesture.startY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    const isHorizontalSwipe = (gesture.direction === 'horizontal' || absDeltaX > absDeltaY * 1.2) && absDeltaX > 50;

    if (isHorizontalSwipe) {
      e.preventDefault();
      if (deltaX > 0) {
        navigateMedia(-1);
      } else {
        navigateMedia(1);
      }
    }

    resetMediaGesture(e.currentTarget);
  }, [navigateMedia, resetMediaGesture]);

  const handleMediaPointerCancel = useCallback((e) => {
    resetMediaGesture(e.currentTarget);
  }, [resetMediaGesture]);

  // Debug logging
  useEffect(() => {
    if (currentMedia) {
      // console.log('[TimelinePage] Current media:', currentMedia, 'type:', currentMedia.type, 'isVideo:', currentMedia.type === 'video');
    }
  }, [currentMedia]);

  return (
    <div className={`event-slide ${isActive ? 'active' : ''}`}>
      {/* Left Panel: Media */}
      <div className="event-slide__images">
        <div
          className={`event-slide__main-image ${mediaItems.length > 1 ? 'event-slide__main-image--interactive' : ''}`}
          onPointerDown={handleMediaPointerDown}
          onPointerMove={handleMediaPointerMove}
          onPointerUp={handleMediaPointerUp}
          onPointerCancel={handleMediaPointerCancel}
        >
          {currentMedia && (
            <>
              {currentMedia.type === 'video' || (currentMedia.url && /\.(mp4|mov|m4v|webm|ogg)$/i.test(currentMedia.url)) ? (
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  poster={currentMedia.posterUrl || undefined}
                  className="event-slide__video"
                  onLoadedMetadata={(event) => {
                    const videoEl = event.currentTarget;
                    if (videoEl.paused) {
                      const playPromise = videoEl.play();
                      if (playPromise && typeof playPromise.catch === 'function') {
                        playPromise.catch(() => {
                          /* Autoplay might be blocked; ignore silently */
                        });
                      }
                    }
                  }}
                  onError={(e) => {
                    console.error('[TimelinePage] Video error:', e, 'src:', currentMedia.url);
                  }}
                >
                  <source src={currentMedia.url} type={currentMedia.format || 'video/mp4'} />
                  Your browser does not support the video tag.
                </video>
              ) : (
                  <img
                    src={currentMedia.url}
                    alt={event.title}
                    draggable="false"
                  />
              )}
              {mediaItems.length > 1 && (
                <>
                  <button
                    className="image-nav image-nav-prev"
                    onClick={() => navigateMedia(-1)}
                    disabled={!canNavigateMediaBack}
                    aria-label="Previous image"
                  >
                    <ChevronLeft size={32} />
                  </button>
                  <button
                    className="image-nav image-nav-next"
                    onClick={() => navigateMedia(1)}
                    disabled={!canNavigateMediaForward}
                    aria-label="Next image"
                  >
                    <ChevronRight size={32} />
                  </button>
                  <div className="image-counter">
                    {activeMediaIndex + 1}/{mediaItems.length}
                  </div>
                </>
              )}
              {currentMedia.caption && (
                <div className="image-caption">
                  © {currentMedia.caption}
                </div>
              )}
            </>
          )}
        </div>

        {/* Thumbnail strip */}
        {mediaItems.length > 1 && (
          <div className="event-slide__thumbnails">
            <button
              className="thumb-nav thumb-prev"
              onClick={() => scrollThumbs('left')}
              aria-label="Scroll thumbnails left"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="thumb-strip" ref={thumbStripRef}>
              {mediaItems.map((media, i) => {
                const thumbSrc = media.thumbnailUrl || media.url;
                return (
                <img
                  key={i}
                    src={thumbSrc}
                    alt={`${event.title} - Media ${i + 1}`}
                    className={i === activeMediaIndex ? 'active' : ''}
                    onClick={() => setActiveMediaIndex(i)}
                  draggable="false"
                />
                );
              })}
            </div>
            <button
              className="thumb-nav thumb-next"
              onClick={() => scrollThumbs('right')}
              aria-label="Scroll thumbnails right"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Right Panel: Metadata */}
      <div className="event-slide__metadata">
        {/* Clean info section - tight label/value pairs (skip for bio slide) */}
        {!event.isBioSlide && (
          <div className="meta-info">
            <div className="meta-info__item">
              <div className="meta-info__item-label">DATE</div>
              <div className="meta-info__item-value">{formatDate(event.date)}</div>
            </div>
            <div className="meta-info__item">
              <div className="meta-info__item-label">LOCATION</div>
              <div className="meta-info__item-value">{event.location}</div>
            </div>
          </div>
        )}

        {/* Title */}
        <div className="meta-title">
          {event.subtype && (
            <span className="meta-title__subtype">{event.subtype}</span>
          )}
          <h1>{event.title}</h1>
        </div>

        {/* Bio Content for Bio Slide */}
        {event.isBioSlide && event.bio && (
          <div className="meta-description">
            {event.bio.sections?.map((section, index) => (
              <div key={index} className="bio-section">
                {section.title && (
                  <h3 className="bio-section-title">{section.title}</h3>
                )}
                <p className="bio-section-text">{section.text}</p>
              </div>
            ))}
          </div>
        )}

        {/* Description for Regular Events */}
        {!event.isBioSlide && event.description && (
          <div className="meta-description">
            <p>{cleanDescription(event.description)}</p>
          </div>
        )}

        {/* Collaborators */}
        {collaborators.length > 0 && (
          <div className="meta-credits">
            <div className="meta-credits__header">CREDITS</div>
            <div className="meta-credits__list">
              {Object.entries(groupedCollaborators).map(([role, collabs]) => {
                const collaboratorNames = collabs.map((collab, i) => {
                  const displayName = collab.link ? (
                    <a
                      key={collab.collaborator_id || i}
                      href={collab.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="meta-credits__link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {collab.name}
                      {collab.link.includes('instagram.com') ? (
                        <span className="meta-credits__icon">📷</span>
                      ) : (
                        <span className="meta-credits__icon">↗</span>
                      )}
                    </a>
                  ) : (
                    <span key={collab.collaborator_id || i}>{collab.name}</span>
                  );
                  return displayName;
                });

                return (
                  <div key={role} className="meta-credits__item">
                    <strong>{role}:</strong>{' '}
                    {collaboratorNames.reduce((acc, name, i) => {
                      if (i === 0) return [name];
                      return [...acc, ', ', name];
                    }, [])}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Press */}
        {relatedPress.length > 0 && (
          <div className="meta-press">
            <div className="meta-press__header">PRESS</div>
            <div className="meta-press__list">
              {relatedPress.slice(0, 5).map((press) => (
                <div
                  key={press.id}
                  className="meta-press__item"
                  onMouseEnter={() => setHoveredPress(press.id)}
                  onMouseLeave={() => setHoveredPress(null)}
                >
                  <strong>{press.source}:</strong> {press.title}

                  {/* Hover preview */}
                  {hoveredPress === press.id && pressImageUrls[press.id] && (
                    <div className="press-preview">
                      <img src={pressImageUrls[press.id]} alt={press.title} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Navigation buttons inside metadata */}
        {showInlineNavigation && (
          <div className="meta-navigation">
            {event.isBioSlide ? (
              // On first slide (bio), show "latest events:" label instead of prev button
              <div className="meta-nav-label">
                <span>latest events:</span>
              </div>
            ) : (
              <button
                className="meta-nav-button meta-nav-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  if (prevEvent && navigateToIndex) {
                    navigateToIndex(currentIndex - 1);
                  }
                }}
                disabled={!prevEvent}
              >
                {prevEvent && (
                  <div className="nav-content">
                    {(() => {
                      const image = getFirstEventImage ? getFirstEventImage(prevEvent) : null;
                      return image ? (
                        <div className="nav-preview">
                          <img
                            src={`/assets/images/${image.jpeg || image.path}`}
                            alt={prevEvent.title}
                          />
                          <ChevronLeft size={24} />
                        </div>
                      ) : null;
                    })()}
                    <div className="nav-info">
                      <span className="nav-title">{prevEvent.title}</span>
                      {!prevEvent.isBioSlide && prevEvent.date && (
                        <span className="nav-date">{formatDate(prevEvent.date)}</span>
                      )}
                    </div>
                  </div>
                )}
              </button>
            )}

            <button
              className="meta-nav-button meta-nav-next"
              onClick={(e) => {
                e.stopPropagation();
                if (nextEvent && navigateToIndex) {
                  navigateToIndex(currentIndex + 1);
                }
              }}
              disabled={!nextEvent}
            >
              {nextEvent && (
                <div className="nav-content">
                  <div className="nav-info">
                    <span className="nav-title">{nextEvent.title}</span>
                    {!nextEvent.isBioSlide && nextEvent.date && (
                      <span className="nav-date">{formatDate(nextEvent.date)}</span>
                    )}
                  </div>
                  {(() => {
                    const image = getFirstEventImage ? getFirstEventImage(nextEvent) : null;
                    return image ? (
                      <div className="nav-preview">
                        <img
                          src={`/assets/images/${image.jpeg || image.path}`}
                          alt={nextEvent.title}
                        />
                        <ChevronRight size={24} />
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

const TimelinePage = ({ data, isEmbedded = false, isAboutSectionActive = true }) => {
  const containerRef = useRef(null);

  // State management
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [allowVerticalScroll, setAllowVerticalScroll] = useState(false);

  // Check if we're on mobile (window width <= 480px) - must be declared before use in callbacks
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 480);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper function to get first image from event (handles preview_images and preview_media)
  const getFirstEventImage = (event) => {
    if (!event) return null;
    if (event.preview_images?.[0]) {
      return event.preview_images[0];
    }
    if (event.preview_media) {
      // First try to find an image (type: 'image' OR has jpeg/path but not a video)
      const firstImage = event.preview_media.find(item => {
        if (item.type === 'image') return true;
        if (item.type === 'video') return false;
        const imagePath = item.jpeg || item.path;
        return imagePath && !imagePath.match(/\.(mp4|mov|m4v|webm|ogg)$/i);
      });
      if (firstImage) {
        return { jpeg: firstImage.jpeg || firstImage.path, path: firstImage.jpeg || firstImage.path };
      }
      // Fallback to video poster
      const firstVideo = event.preview_media.find(item => item.type === 'video');
      if (firstVideo && firstVideo.poster) {
        return { jpeg: firstVideo.poster, path: firstVideo.poster };
      }
    }
    return null;
  };

  // Extract events from data and create bio slide
  const rawEvents = data?.events || [];
  const bioData = data?.bio;

  // Create a bio slide if bio data exists
  const bioSlide = bioData ? {
    id: 'bio-slide',
    title: 'ABOUT ELODIE CARSTENSEN',
    date: '',
    location: '',
    description: '',
    preview_images: bioData.image ? [{
      jpeg: bioData.image,
      caption: 'Photo by\nThai Hoang @loewe7'
    }] : [],
    bio: bioData,
    isBioSlide: true
  } : null;

  // Combine bio slide with events
  const events = bioSlide ? [bioSlide, ...rawEvents] : rawEvents;
  const pressItems = data?.press_mentions || [];


  // Load initial index from URL
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Try to find event by title slug first (stable identifier)
    const eventSlug = params.get('event');
    if (eventSlug) {
      const foundIndex = events.findIndex(event =>
        !event.isBioSlide && slugify(event.title) === eventSlug
      );
      if (foundIndex !== -1) {
        setCurrentIndex(foundIndex);
        return;
      }
    }

    // Fallback to index parameter for backwards compatibility
    const eventParam = parseInt(params.get('e'));
    if (!isNaN(eventParam) && eventParam >= 0 && eventParam < events.length) {
      setCurrentIndex(eventParam);
    }
  }, [location.search, events.length]);

  // Update URL when index changes - but only when NOT embedded (or after user interaction)
  // When embedded in CollectionsPage, don't update URL on initial mount
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  useEffect(() => {
    // Don't update URL if embedded and user hasn't interacted yet
    if (isEmbedded && !hasUserInteracted) {
      debugLog('[TimelinePage] Skipping URL update - embedded and no user interaction yet');
      return;
    }

    // Don't update URL if embedded and about section is not active (user scrolled away)
    if (isEmbedded && !isAboutSectionActive) {
      debugLog('[TimelinePage] Skipping URL update - about section not active (scrolled away)');
      return;
    }

    const currentEvent = events[currentIndex];
    let newUrl = '/about';

    if (currentIndex > 0 && currentEvent && !currentEvent.isBioSlide && currentEvent.title) {
      // Use title slug as primary identifier (stable across event additions)
      const eventSlug = slugify(currentEvent.title);
      if (eventSlug) {
        const params = new URLSearchParams();
        params.set('event', eventSlug);
        newUrl = `/about?${params.toString()}`;
      }
    }

    debugLog('[TimelinePage] Updating URL to:', newUrl, { isEmbedded, hasUserInteracted, isAboutSectionActive, currentIndex });
    window.history.replaceState(null, '', newUrl);
  }, [currentIndex, events, isEmbedded, hasUserInteracted, isAboutSectionActive]);

  // Navigation function
  const navigateToIndex = useCallback((newIndex) => {
    if (newIndex < 0 || newIndex >= events.length || isTransitioning) {
      return;
    }

    // Mark user interaction when navigating
    if (!hasUserInteracted) {
      debugLog('[TimelinePage] User interacted - enabling URL updates');
      setHasUserInteracted(true);
    }

    setIsTransitioning(true);
    setCurrentIndex(newIndex);

    // Reset vertical scroll position when navigating between events (both mobile and desktop)
    const timelineContainer = containerRef.current;
    if (timelineContainer) {
      // Immediate scroll to top for instant feedback
      timelineContainer.scrollTo({ top: 0, behavior: 'auto' });
    }

    setTimeout(() => setIsTransitioning(false), 400); // Match CSS transition duration
  }, [events.length, hasUserInteracted, isTransitioning]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        navigateToIndex(currentIndex - 1);
      } else if (e.key === 'ArrowRight') {
        navigateToIndex(currentIndex + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, navigateToIndex]);

  // Update allowVerticalScroll based on current index
  useEffect(() => {
    // Allow vertical scroll when at first event (scroll up) or last event (scroll down)
    setAllowVerticalScroll(currentIndex === 0 || currentIndex === events.length - 1);
  }, [currentIndex, events.length]);

  if (!data || events.length === 0) {
    return <Loading />;
  }

  // Calculate transform (only for desktop)
  const baseTransform = -currentIndex * 100;

  const prevEvent = currentIndex > 0 ? events[currentIndex - 1] : null;
  const nextEvent = currentIndex < events.length - 1 ? events[currentIndex + 1] : null;

  return (
    <>
      <MetaTags {...getAboutMetaData()} />
      <div
        className={`timeline-page ${allowVerticalScroll ? 'allow-vertical-scroll' : ''}`}
        ref={containerRef}
      >
        <div
          className="timeline-container"
          style={isMobile ? {} : {
            transform: `translateX(${baseTransform}vw)`,
            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {isMobile ? (
            // On mobile, only render the current event
            <EventSlide
              key={events[currentIndex].id}
              event={events[currentIndex]}
              isActive={true}
              pressItems={pressItems}
              isMobile={isMobile}
              events={events}
              navigateToIndex={navigateToIndex}
              getFirstEventImage={getFirstEventImage}
              slugify={slugify}
              formatDate={formatDate}
              prevEvent={prevEvent}
              nextEvent={nextEvent}
              currentIndex={currentIndex}
              eventsLength={events.length}
              showInlineNavigation={!isMobile}
            />
          ) : (
            // On desktop, render all events with transform
            events.map((event, index) => (
              <EventSlide
                key={event.id}
                event={event}
                isActive={index === currentIndex}
                pressItems={pressItems}
                isMobile={isMobile}
                events={events}
                navigateToIndex={navigateToIndex}
                getFirstEventImage={getFirstEventImage}
                slugify={slugify}
                formatDate={formatDate}
                prevEvent={index > 0 ? events[index - 1] : null}
                nextEvent={index < events.length - 1 ? events[index + 1] : null}
                currentIndex={index}
                eventsLength={events.length}
                showInlineNavigation={true}
              />
            ))
          )}
        </div>
        {isMobile && (!isEmbedded || isAboutSectionActive) && (
          <div className="timeline-floating-nav" aria-label="Event navigation">
            <button
              type="button"
              className="timeline-floating-nav__button timeline-floating-nav__button--prev"
              onClick={() => navigateToIndex(currentIndex - 1)}
              disabled={!prevEvent}
              aria-label={prevEvent ? `Previous event: ${prevEvent.title}` : 'Previous event'}
            >
              <ChevronLeft size={18} />
              {prevEvent && (
                <span className="timeline-floating-nav__info">
                  <span className="timeline-floating-nav__title">{prevEvent.title}</span>
                  {!prevEvent.isBioSlide && prevEvent.date && (
                    <span className="timeline-floating-nav__date">{formatDate(prevEvent.date)}</span>
                  )}
                </span>
              )}
            </button>
            <button
              type="button"
              className="timeline-floating-nav__button timeline-floating-nav__button--next"
              onClick={() => navigateToIndex(currentIndex + 1)}
              disabled={!nextEvent}
              aria-label={nextEvent ? `Next event: ${nextEvent.title}` : 'Next event'}
            >
              {nextEvent && (
                <span className="timeline-floating-nav__info">
                  <span className="timeline-floating-nav__title">{nextEvent.title}</span>
                  {!nextEvent.isBioSlide && nextEvent.date && (
                    <span className="timeline-floating-nav__date">{formatDate(nextEvent.date)}</span>
                  )}
                </span>
              )}
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default TimelinePage;
